import { useEffect, useState } from 'react';
import Seo from '../../components/Seo';
import { Field, PageLoader, ErrorState } from '../../components/ui';
import { menusApi, pagesApi } from '../../api/resources';
import { useToast } from '../../context/ToastContext';

/**
 * Navigation / Menu Management.
 *
 * Manages the CMS-driven header menu. These items are APPENDED after the static
 * primary navigation in the site header (they never replace it). Admins can link
 * to a published CMS page (via dynamic_page_id) or to a custom URL.
 */
export default function MenuManager() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [menuId, setMenuId] = useState(null);
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState([]);

  // Add-link form state
  const [linkType, setLinkType] = useState('page'); // 'page' | 'custom'
  const [pageId, setPageId] = useState('');
  const [pageLabel, setPageLabel] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customUrl, setCustomUrl] = useState('');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [menus, pageList] = await Promise.all([
        menusApi.list('header'),
        pagesApi.list({ per_page: 100 }),
      ]);

      const headerMenu = Array.isArray(menus)
        ? menus.find((m) => m.location === 'header') || menus[0]
        : null;

      if (headerMenu) {
        setMenuId(headerMenu.id);
        setItems(
          (headerMenu.items || []).map((it) => ({
            key: `srv-${it.id}`,
            label: it.label,
            target: it.target || '_self',
            dynamic_page_id: it.dynamic_page_id || null,
            url: it.dynamic_page_id ? '' : it.url || '',
            resolved_url: it.resolved_url || it.url || '',
          }))
        );
      } else {
        setMenuId(null);
        setItems([]);
      }

      const published = (pageList.items || []).filter((p) => p.status === 'published');
      setPages(published);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function addPageLink() {
    if (!pageId) {
      toast.error('Select a published page.');
      return;
    }
    const page = pages.find((p) => String(p.id) === String(pageId));
    if (!page) return;
    const label = pageLabel.trim() || page.title;
    setItems((arr) => [
      ...arr,
      {
        key: `new-${Date.now()}`,
        label,
        target: '_self',
        dynamic_page_id: page.id,
        url: '',
        resolved_url: `/pages/${page.slug}`,
      },
    ]);
    setPageId('');
    setPageLabel('');
  }

  function addCustomLink() {
    const label = customLabel.trim();
    const url = customUrl.trim();
    if (!label || !url) {
      toast.error('Label and URL are required.');
      return;
    }
    setItems((arr) => [
      ...arr,
      {
        key: `new-${Date.now()}`,
        label,
        target: '_self',
        dynamic_page_id: null,
        url,
        resolved_url: url,
      },
    ]);
    setCustomLabel('');
    setCustomUrl('');
  }

  function remove(key) {
    setItems((arr) => arr.filter((x) => x.key !== key));
  }

  function move(key, dir) {
    setItems((arr) => {
      const i = arr.findIndex((x) => x.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= arr.length) return arr;
      const copy = [...arr];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }

  function setTarget(key, target) {
    setItems((arr) => arr.map((x) => (x.key === key ? { ...x, target } : x)));
  }

  async function save() {
    setSaving(true);
    try {
      let id = menuId;
      if (!id) {
        const created = await menusApi.create({
          name: 'Primary',
          location: 'header',
          is_active: true,
        });
        id = created?.id;
        setMenuId(id);
      }
      if (!id) throw new Error('Could not resolve a menu to save into.');

      await menusApi.update(id, {
        items: items.map((it, i) => ({
          label: it.label,
          url: it.url || undefined,
          dynamic_page_id: it.dynamic_page_id || undefined,
          target: it.target || '_self',
          position: i,
          is_active: true,
        })),
      });
      toast.success('Menu saved.');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not save menu.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <PageLoader label="Loading menu…" />;
  if (error) return <ErrorState error={error} onRetry={load} />;

  return (
    <div className="space-y-6">
      <Seo title="Menu Management" />
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Menu Management</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-muted">
            These links appear in the site header after the main navigation. Only published pages
            can be linked.
          </p>
        </div>
        <button className="btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Menu'}
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Current items */}
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">Header Menu Items</h2>
          {items.length === 0 ? (
            <p className="rounded-lg border border-dashed border-cream-300 bg-surface-alt px-4 py-8 text-center text-sm text-ink-muted">
              No appended links yet. Add a published page or custom URL on the right.
            </p>
          ) : (
            <ul className="space-y-2">
              {items.map((item, i) => (
                <li
                  key={item.key}
                  className="flex items-center justify-between gap-3 rounded-lg border border-cream-300 bg-surface-alt px-4 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">{item.label}</p>
                    <p className="truncate text-xs text-ink-muted">
                      {item.resolved_url || item.url || '—'}
                      {item.dynamic_page_id ? (
                        <span className="pill ml-2 align-middle text-[10px]">Page</span>
                      ) : (
                        <span className="pill ml-2 align-middle text-[10px]">Custom</span>
                      )}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <select
                      className="input !h-8 !w-auto !py-0 text-xs"
                      value={item.target}
                      onChange={(e) => setTarget(item.key, e.target.value)}
                      aria-label="Link target"
                    >
                      <option value="_self">Same tab</option>
                      <option value="_blank">New tab</option>
                    </select>
                    <button
                      onClick={() => move(item.key, -1)}
                      disabled={i === 0}
                      className="btn-ghost px-2 py-1 text-xs"
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => move(item.key, 1)}
                      disabled={i === items.length - 1}
                      className="btn-ghost px-2 py-1 text-xs"
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => remove(item.key)}
                      className="btn-ghost px-2 py-1 text-xs text-red-600"
                      aria-label="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add link */}
        <div className="card h-fit p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">Add Link</h2>

          <div className="mb-4 inline-flex rounded-full border border-cream-300 bg-surface-alt p-1">
            <button
              type="button"
              onClick={() => setLinkType('page')}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                linkType === 'page' ? 'bg-maroon-700 text-white' : 'text-ink-soft'
              }`}
            >
              Page
            </button>
            <button
              type="button"
              onClick={() => setLinkType('custom')}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                linkType === 'custom' ? 'bg-maroon-700 text-white' : 'text-ink-soft'
              }`}
            >
              Custom URL
            </button>
          </div>

          {linkType === 'page' ? (
            <div className="space-y-4">
              <Field label="Published page" hint={pages.length === 0 ? 'No published pages available yet.' : undefined}>
                <select
                  className="input"
                  value={pageId}
                  onChange={(e) => {
                    setPageId(e.target.value);
                    const p = pages.find((x) => String(x.id) === String(e.target.value));
                    setPageLabel(p ? p.title : '');
                  }}
                  disabled={pages.length === 0}
                >
                  <option value="">Select a page…</option>
                  {pages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Label">
                <input
                  className="input"
                  placeholder="Defaults to page title"
                  value={pageLabel}
                  onChange={(e) => setPageLabel(e.target.value)}
                />
              </Field>
              <button onClick={addPageLink} className="btn-outline w-full" disabled={!pageId}>
                + Add page link
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <Field label="Label">
                <input
                  className="input"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                />
              </Field>
              <Field label="URL" hint="Internal path (/about) or external (https://…).">
                <input
                  className="input"
                  placeholder="https://example.org"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                />
              </Field>
              <button onClick={addCustomLink} className="btn-outline w-full">
                + Add custom link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
